import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import BoardGrid from '../components/BoardGrid';
import InitialsAvatar from '../components/InitialsAvatar';
import { PageLoader, ErrorState } from '../components/ui';
import { useAsync } from '../lib/useAsync';
import { membersApi } from '../api/resources';

function FeaturedLeader({ member }) {
  return (
    <Reveal as="article" className="card overflow-hidden">
      <div className="grid gap-8 p-8 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-10 sm:p-10">
        <div className="relative mx-auto sm:mx-0">
          <div
            aria-hidden="true"
            className="absolute -bottom-3 -right-3 h-20 w-20 rounded-full border-[6px] border-gold-400"
          />
          <div className="h-40 w-40 overflow-hidden rounded-full ring-4 ring-maroon-50 shadow-card sm:h-48 sm:w-48">
            <InitialsAvatar name={member.full_name} photoPath={member.photo_url || member.photo_path} size="lg" />
          </div>
        </div>
        <div className="text-center sm:text-left">
          {member.position && (
            <p className="eyebrow mb-2">{member.position}</p>
          )}
          <h2 className="font-display text-3xl font-extrabold leading-tight text-ink sm:text-4xl">
            {member.full_name}
          </h2>
          {member.club_name && (
            <p className="mt-1 text-sm font-medium text-ink-muted">{member.club_name}</p>
          )}
          {member.bio && (
            <p className="mt-4 max-w-2xl leading-relaxed text-ink-soft">{member.bio}</p>
          )}
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="mt-4 inline-block text-sm font-semibold text-maroon-700 hover:text-gold-600"
            >
              {member.email}
            </a>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Leadership() {
  const { data, loading, error, reload } = useAsync(
    () => membersApi.list({ leadership: 1, per_page: 20 }),
    []
  );
  const leaders = data?.items || [];

  // Render in API order (board_order ASC, admin-managed). The first item is the
  // featured leader (#1); the remainder fills the board grid.
  const featured = leaders[0] || null;
  const rest = featured ? leaders.slice(1) : [];

  return (
    <>
      <Seo
        title="Area 1 Officers & Presidents"
        description="Meet the Area 1 officers and club presidents leading Zonta District 17 Area 1 — their roles and backgrounds."
      />

      <PageHero
        eyebrow="Our People"
        title="The women leading the way"
        subtitle="Dedicated leaders guiding Zonta District 17 Area 1 through service and advocacy."
        image="/img/hero-leader.jpg"
        caption={{ name: 'Fernanda Gallo Freschi', role: 'Zonta International President' }}
      />

      <section className="section">
        <div className="container-page">
          {loading ? (
            <PageLoader label="Loading board…" />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : !leaders.length ? (
            <p className="text-center text-ink-muted">Board details are coming soon.</p>
          ) : (
            <div className="space-y-12">
              {featured && <FeaturedLeader member={featured} />}
              {rest.length > 0 && (
                <div>
                  <h2 className="heading-brand mb-8 text-2xl sm:text-3xl">
                    Area 1: Clubs and Presidents
                  </h2>
                  <BoardGrid members={rest} />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
